import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatPhotoCaption } from "@/lib/photoCaption";

const GOLD = [212, 175, 55];
const DARK = [16, 20, 34];
const GREY = [130, 130, 130];

const CONDITION_LABEL = { excellent: "Excellent", good: "Good", needs_attention: "Needs Attention", critical: "Critical" };
const CLEAN_LABEL = { very_clean: "Very Clean", clean: "Clean", needs_cleaning: "Needs Cleaning", dust_present: "Dust Present", dirty: "Dirty", critical: "Critical Cleaning" };
const DUST_LABEL = { none: "No Visible Dust", light: "Light Dust", moderate: "Moderate Dust", heavy: "Heavy Dust" };

async function loadLogo() {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/hos-logo.webp";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } catch (_) { return null; }
}

/** Fetch an object-storage photo with auth and return a JPEG data URL for jsPDF.
 *  Falls back to the inline data_url if present (legacy records). */
async function photoToDataUrl(p) {
  if (!p) return null;
  if (p.data_url) return p.data_url;
  if (!p.url) return null;
  try {
    const token = window.localStorage.getItem("hos_token");
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const abs = p.url.startsWith("http") ? p.url : `${backend}${p.url}`;
    const resp = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch (_) { return null; }
}

function drawHeader(doc, dc, logo) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...DARK); doc.rect(0, 0, w, 26, "F");
  if (logo) doc.addImage(logo, "PNG", 12, 5, 16, 16);
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("HOUSE OF SPELLS — DISPLAY CHECK REPORT", 32, 12);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`${dc.check_number} · ${dc.store_name} · ${dc.inspection_date}`, 32, 19);
}

function drawFooter(doc, page, total) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setTextColor(...GREY); doc.setFontSize(8);
  doc.text(`Page ${page} of ${total}`, w - 25, h - 8);
  doc.text(`Generated ${new Date().toLocaleString()}`, 12, h - 8);
}

export default async function generateDisplayCheckPdf(dc) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logo = await loadLogo();
  drawHeader(doc, dc, logo);

  let y = 34;
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text(`Display Check ${dc.check_number}`, 12, y);
  y += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const meta = [
    ["Store", dc.store_name],
    ["Inspection Date", dc.inspection_date],
    ["Manager", dc.manager_name],
    ["Status", (dc.status || "").replace(/_/g, " ").toUpperCase()],
    ["Reviewed By", dc.reviewed_by_name || "—"],
    ["Zones Inspected", `${(dc.zones || []).filter((z) => ["completed", "corrected"].includes(z.status)).length} / ${(dc.zones || []).length}`],
  ];
  autoTable(doc, {
    startY: y, theme: "plain",
    styles: { fontSize: 9, cellPadding: 1.5 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 40, textColor: [60, 60, 60] } },
    body: meta,
  });
  y = doc.lastAutoTable.finalY + 6;

  if (dc.instructions) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...DARK);
    doc.text("Instructions", 12, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(dc.instructions, 186);
    doc.text(lines, 12, y);
    y += lines.length * 4 + 4;
  }

  // Zone summary table
  const zoneRows = (dc.zones || []).map((z, i) => [
    i + 1,
    z.zone_name,
    (z.zone_owner_names || []).join(", ") || "—",
    (z.fandom_names || []).join(", ") || "—",
    CONDITION_LABEL[z.condition] || "—",
    CLEAN_LABEL[z.cleanliness_status] || "—",
    DUST_LABEL[z.dust_condition] || "—",
    (z.issues || []).length,
    (z.photos || []).length,
    (z.status || "").replace(/_/g, " "),
  ]);
  autoTable(doc, {
    startY: y,
    head: [["#", "Zone", "Zone Owners", "Fandoms", "Condition", "Cleanliness", "Dust", "Issues", "Photos", "Status"]],
    body: zoneRows,
    headStyles: { fillColor: DARK, textColor: GOLD, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 7.5, cellPadding: 1.5 },
    columnStyles: { 0: { cellWidth: 7 }, 7: { halign: "center" }, 8: { halign: "center" } },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Per-zone detail pages
  for (const z of dc.zones || []) {
    doc.addPage(); drawHeader(doc, dc, logo);
    let yy = 34;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...DARK);
    doc.text(z.zone_name, 12, yy); yy += 7;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    doc.text(`Condition: ${CONDITION_LABEL[z.condition] || "—"} · Fandoms: ${(z.fandom_names || []).join(", ") || "—"}`, 12, yy);
    yy += 6;
    // Zone Owners (per this specific inspection)
    if ((z.zone_owner_names || []).length > 0) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
      doc.text("Zone Owner(s) — this inspection", 12, yy); yy += 4;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
      doc.text(z.zone_owner_names.join(", "), 12, yy); yy += 6;
    }
    // Cleanliness block
    if (z.cleanliness_status || z.dust_condition || Object.keys(z.cleanliness_checklist || {}).length > 0) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
      doc.text("Cleanliness", 12, yy); yy += 4;
      const cleanRows = [];
      if (z.cleanliness_status) cleanRows.push(["Overall", CLEAN_LABEL[z.cleanliness_status] || z.cleanliness_status]);
      if (z.dust_condition) cleanRows.push(["Dust", DUST_LABEL[z.dust_condition] || z.dust_condition]);
      if (typeof z.cleanliness_score === "number") cleanRows.push(["Score", `${z.cleanliness_score}%`]);
      Object.entries(z.cleanliness_checklist || {}).forEach(([k, v]) => {
        cleanRows.push([k.replace(/_/g, " "), v === true ? "Yes" : v === false ? "No" : "N/A"]);
      });
      if (z.cleanliness_notes) cleanRows.push(["Notes", z.cleanliness_notes]);
      autoTable(doc, {
        startY: yy, theme: "plain", body: cleanRows,
        styles: { fontSize: 8.5, cellPadding: 1.4 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 40, textColor: [60, 60, 60] } },
      });
      yy = doc.lastAutoTable.finalY + 4;
    }
    if (z.correction_reason) {
      doc.setTextColor(180, 60, 40);
      doc.text(`Correction requested: ${z.correction_reason}`, 12, yy); yy += 6;
      doc.setTextColor(80, 80, 80);
    }
    // Checklist
    const ckRows = Object.entries(z.checklist || {}).map(([k, v]) => [k.replace(/_/g, " "), v ? "Yes" : "No"]);
    if (ckRows.length) {
      autoTable(doc, {
        startY: yy, head: [["Checklist Item", "Result"]], body: ckRows,
        headStyles: { fillColor: DARK, textColor: GOLD, fontSize: 9 },
        styles: { fontSize: 8.5, cellPadding: 1.5 },
        columnStyles: { 1: { cellWidth: 30, halign: "center" } },
      });
      yy = doc.lastAutoTable.finalY + 4;
    }
    // Issues / comments
    const notes = [];
    if ((z.issues || []).length) notes.push(["Issues", z.issues.join(", ")]);
    if (z.other_issue_detail) notes.push(["Other Issue", z.other_issue_detail]);
    if (z.comments) notes.push(["Comments", z.comments]);
    if (z.action_taken) notes.push(["Action Taken", z.action_taken]);
    if (notes.length) {
      autoTable(doc, {
        startY: yy, body: notes, theme: "plain",
        styles: { fontSize: 9, cellPadding: 1.5 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 35, textColor: [60, 60, 60] } },
      });
      yy = doc.lastAutoTable.finalY + 4;
    }
    // Photos — show ALL, wrapping to multiple rows and pages as needed
    const originals = (z.photos || []).filter((p) => (p.revision || 0) === 0);
    const corrections = (z.photos || []).filter((p) => (p.revision || 0) >= 1);
    const pageH = doc.internal.pageSize.getHeight();
    const bottomMargin = 18;
    const PER_ROW = 3;
    const size = 58;
    const gap = 5;
    const captionH = 7;
    const commentH = 4; // per comment line rendered under the caption
    const drawPhotos = async (label, photos, groupByArea) => {
      if (!photos.length) return;
      let groups;
      if (groupByArea) {
        const m = new Map();
        for (const p of photos) {
          const code = p.display_area_code_snapshot || p.display_area_code || "Unassigned";
          const name = p.display_area_name_snapshot || p.display_area_name || "";
          if (!m.has(code)) m.set(code, { key: code, name, photos: [] });
          m.get(code).photos.push(p);
        }
        groups = [...m.values()];
      } else {
        groups = [{ key: "", name: "", photos }];
      }
      for (const g of groups) {
        if (yy + 8 > pageH - bottomMargin) {
          doc.addPage(); drawHeader(doc, dc, logo); yy = 34;
        }
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
        const groupLabel = g.key ? `${label} — ${g.key}${g.name ? ` (${g.name})` : ""}` : label;
        doc.text(groupLabel, 12, yy); yy += 4;
        // Compute a per-row height that fits the tallest photo's caption + up to 2 comment lines.
        for (let i = 0; i < g.photos.length; i++) {
          const p = g.photos[i];
          const col = i % PER_ROW;
          if (col === 0 && i > 0) {
            // Row height uses the max comment count in this row (max 2 lines shown).
            const rowStart = i - PER_ROW;
            const rowMaxCmts = Math.min(2, Math.max(0, ...g.photos.slice(rowStart, i).map((q) => (q.comments || []).length)));
            yy += size + captionH + rowMaxCmts * commentH + gap;
          }
          const nCmts = Math.min(2, (p.comments || []).length);
          if (yy + size + captionH + nCmts * commentH > pageH - bottomMargin) {
            doc.addPage(); drawHeader(doc, dc, logo); yy = 34;
            doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
            doc.text(`${groupLabel} (continued)`, 12, yy); yy += 4;
          }
          const x = 12 + col * (size + gap);
          const src = await photoToDataUrl(p);
          if (src) {
            try { doc.addImage(src, "JPEG", x, yy, size, size); } catch (_) {}
          }
          const cap = formatPhotoCaption(p);
          const primary = doc.splitTextToSize(cap.primary || "", size)[0] || "";
          const secondary = doc.splitTextToSize(cap.secondary || "", size)[0] || "";
          if (primary) {
            doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(...DARK);
            doc.text(primary, x, yy + size + 2.6);
          }
          if (secondary) {
            doc.setFont("helvetica", "normal"); doc.setFontSize(5.6); doc.setTextColor(...GREY);
            doc.text(secondary, x, yy + size + 5.6);
          }
          // Review comments (max 2 lines) — flagged with "» author: text"
          const cmts = (p.comments || []).slice(0, 2);
          if (cmts.length) {
            doc.setFont("helvetica", "italic"); doc.setFontSize(5.4); doc.setTextColor(...GREY);
            cmts.forEach((c, ci) => {
              const line = doc.splitTextToSize(`» ${c.author_name}: ${c.text}`, size)[0] || "";
              doc.text(line, x, yy + size + 8.4 + ci * commentH);
            });
            const more = (p.comments || []).length - cmts.length;
            if (more > 0) {
              const line = `+ ${more} more comment${more === 1 ? "" : "s"}`;
              doc.text(line, x, yy + size + 8.4 + cmts.length * commentH);
            }
          }
        }
        // Advance yy past the last row.
        const totalPhotos = g.photos.length;
        const lastRowStart = totalPhotos - (totalPhotos % PER_ROW || PER_ROW);
        const lastRowMaxCmts = Math.min(2, Math.max(0, ...g.photos.slice(lastRowStart).map((q) => (q.comments || []).length)));
        yy += size + captionH + lastRowMaxCmts * commentH + 6;
      }
    };
    await drawPhotos(corrections.length ? "MAIN ZONE PHOTOS — BEFORE" : "MAIN ZONE PHOTOS", originals, true);
    await drawPhotos("AFTER (correction)", corrections, true);
    // Per-issue photos
    const issuePhotos = z.issue_photos || {};
    for (const ik of (z.issues || [])) {
      const arr = issuePhotos[ik] || [];
      const label = `ISSUE: ${ik.replace(/_/g, " ").toUpperCase()}${arr.length === 0 ? " (no photos)" : ""}`;
      if (arr.length === 0) {
        if (yy + 6 > pageH - bottomMargin) { doc.addPage(); drawHeader(doc, dc, logo); yy = 34; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
        doc.text(label, 12, yy); yy += 6;
      } else {
        await drawPhotos(label, arr);
      }
    }
    // AI verdict, if present
    if (z.ai_compare) {
      const v = z.ai_compare;
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
      doc.text(`AI Verification: ${v.fixed ? "FIXED" : v.fixed === false ? "NOT FIXED" : "INCONCLUSIVE"} (confidence ${(v.confidence * 100).toFixed(0)}%)`, 12, yy);
      yy += 4;
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(80, 80, 80);
      const notesL = doc.splitTextToSize(v.notes || "", 186);
      doc.text(notesL, 12, yy); yy += notesL.length * 3.6;
    }
  }

  // Footer pages
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); drawFooter(doc, i, total); }
  doc.save(`display-check-${dc.check_number}.pdf`);
}
